/**
 * Origin-wide mutex для операций, меняющих cookie-сессию и общую
 * PushSubscription. Без него поздний logout A мог отозвать уже
 * новую cookie/push-подписку B из другой вкладки.
 */

import {
  invalidateApiSessionRequests,
  resumeApiSessionRequests,
  setApiSessionOwner,
  suspendApiSessionRequests,
} from '@shared/api/client'
import { closeUserDb, deviceDb } from '@shared/db'
import { uuid } from '@shared/lib/uuid'
import { setSyncEngine, syncEngine } from '@shared/sync/instance'
import { postTabMessage, releaseLeadership, TAB_ID, type TabMessage } from '@shared/ws/leader'

const AUTH_TRANSITION_LOCK = 'dvizh-auth-transition'
const FALLBACK_LOCK_KEY = 'auth_transition_lock'
const FALLBACK_LEASE_MS = 15_000
const FALLBACK_RENEW_MS = 5_000
const FALLBACK_RETRY_MS = 100
const AUTH_TRANSITION_EVENT_KEY = 'dvizh-auth-transition-event'
const FALLBACK_TRACE_FLAG = 'dvizh-device-quality-auth-trace'
const FALLBACK_TRACE_KEY = 'dvizh-device-quality-auth-events'
const FALLBACK_RETIRED_PREFIX = 'dvizh-auth-transition-retired:'
const FALLBACK_TRACE_BUILD = import.meta.env.MODE === 'e2e'

type AuthTransitionMessage = Extract<
  TabMessage,
  { type: 'auth_transition_started' | 'auth_transition_complete' }
>

export type AuthTransitionGuard = () => Promise<void>

interface StoredAuthTransition {
  message: AuthTransitionMessage
  nonce: string
  sent_at: number
}

interface FallbackLease {
  owner: string
  expires_at: number
}

function traceFallback(owner: string, stage: string, detail?: string): void {
  try {
    if (!FALLBACK_TRACE_BUILD && sessionStorage.getItem(FALLBACK_TRACE_FLAG) !== '1') return
    const parsed = JSON.parse(sessionStorage.getItem(FALLBACK_TRACE_KEY) ?? '[]') as unknown
    const events = Array.isArray(parsed) ? parsed.slice(-79) : []
    events.push({ owner, stage, detail: detail ?? null, at: Date.now() })
    sessionStorage.setItem(FALLBACK_TRACE_KEY, JSON.stringify(events))
  } catch {
    /* Device-only diagnostics must never affect auth. */
  }
}

function fallbackRetiredKey(owner: string): string {
  return `${FALLBACK_RETIRED_PREFIX}${owner}`
}

/**
 * `pagehide` can freeze WebKit before an async IndexedDB `finally` runs. A
 * synchronous storage tombstone lets the next document distinguish that dead
 * lease from a live tab instead of waiting for its full TTL.
 */
function retireFallback(owner: string): boolean {
  try {
    const key = fallbackRetiredKey(owner)
    const expiresAt = Date.now() + FALLBACK_LEASE_MS * 2
    localStorage.setItem(key, String(expiresAt))
    const stored = Number(localStorage.getItem(key))
    if (Number.isFinite(stored) && stored >= expiresAt) {
      traceFallback(owner, 'retired')
      return true
    }
  } catch {
    /* TTL remains the safe fallback when storage is unavailable. */
  }
  return false
}

function isFallbackRetired(owner: string): boolean {
  try {
    const key = fallbackRetiredKey(owner)
    const expiresAt = Number(localStorage.getItem(key))
    if (Number.isFinite(expiresAt) && expiresAt > Date.now()) return true
    localStorage.removeItem(key)
  } catch {
    /* Treat an unreadable tombstone as absent; the lease TTL still applies. */
  }
  return false
}

function clearFallbackRetired(owner: string): void {
  try {
    localStorage.removeItem(fallbackRetiredKey(owner))
  } catch {
    /* The marker expires by value and is ignored after its short lifetime. */
  }
}

async function tryAcquireFallback(owner: string): Promise<boolean> {
  // An interrupted document must never revive its own lease after pageshow or
  // after a delayed IndexedDB transaction resumes from WebKit's freeze.
  if (isFallbackRetired(owner)) return false
  return deviceDb.transaction('rw', deviceDb.device, async () => {
    // `pagehide` may run while this IndexedDB transaction is queued. Recheck
    // the synchronous marker inside the transaction so an old document does
    // not create a fresh lease after it has already been retired.
    if (isFallbackRetired(owner)) return false
    const current = await deviceDb.device.get(FALLBACK_LOCK_KEY)
    const lease = current?.value as FallbackLease | undefined
    if (lease && lease.owner !== owner && lease.expires_at > Date.now() && !isFallbackRetired(lease.owner)) {
      return false
    }
    if (isFallbackRetired(owner)) return false
    await deviceDb.device.put({
      key: FALLBACK_LOCK_KEY,
      value: { owner, expires_at: Date.now() + FALLBACK_LEASE_MS } satisfies FallbackLease,
    })
    return true
  })
}

/** Renewal is validation-only: a stale owner must never reacquire an empty lock. */
async function renewFallback(owner: string): Promise<boolean> {
  if (isFallbackRetired(owner)) return false
  return deviceDb.transaction('rw', deviceDb.device, async () => {
    const current = await deviceDb.device.get(FALLBACK_LOCK_KEY)
    const lease = current?.value as FallbackLease | undefined
    if (!lease || lease.owner !== owner) return false
    await deviceDb.device.put({
      key: FALLBACK_LOCK_KEY,
      value: { owner, expires_at: Date.now() + FALLBACK_LEASE_MS } satisfies FallbackLease,
    })
    return true
  })
}

async function releaseFallback(owner: string): Promise<void> {
  await deviceDb.transaction('rw', deviceDb.device, async () => {
    const current = await deviceDb.device.get(FALLBACK_LOCK_KEY)
    if ((current?.value as FallbackLease | undefined)?.owner === owner) {
      await deviceDb.device.delete(FALLBACK_LOCK_KEY)
    }
  })
}

async function releaseFallbackReliably(owner: string): Promise<boolean> {
  let lastError: unknown
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      await releaseFallback(owner)
      return true
    } catch (error) {
      lastError = error
      if (attempt < 2) await new Promise<void>((resolve) => setTimeout(resolve, FALLBACK_RETRY_MS))
    }
  }

  // A verified synchronous tombstone is an equivalent handoff barrier: a new
  // document can atomically replace this lease and the old owner can no longer
  // renew it. Without either IDB release or storage retirement, fail closed.
  if (retireFallback(owner)) return false
  throw lastError ?? new Error('auth transition fallback release failed')
}

function abortError(signal: AbortSignal): unknown {
  return signal.reason ?? new DOMException('auth transition interrupted', 'AbortError')
}

function throwIfAborted(signal: AbortSignal): void {
  if (signal.aborted) throw abortError(signal)
}

async function waitForFallbackRetry(signal: AbortSignal): Promise<void> {
  throwIfAborted(signal)
  await new Promise<void>((resolve, reject) => {
    const timer = setTimeout(done, FALLBACK_RETRY_MS)
    const onAbort = (): void => {
      clearTimeout(timer)
      signal.removeEventListener('abort', onAbort)
      reject(abortError(signal))
    }
    function done(): void {
      signal.removeEventListener('abort', onAbort)
      resolve()
    }
    signal.addEventListener('abort', onAbort, { once: true })
  })
}

async function withFallbackMutex<T>(
  task: (ensureOwned: AuthTransitionGuard) => Promise<T>,
  signal: AbortSignal,
  onAcquired: () => void,
  diagnosticName: string,
): Promise<T> {
  const owner = uuid()
  traceFallback(owner, 'created', diagnosticName)
  // Register before the first IndexedDB await. WebKit can freeze the page as
  // soon as `pagehide` returns, so installing this after acquisition leaves a
  // window where the transaction writes a lease without a tombstone.
  const retireOnAbort = (): void => {
    retireFallback(owner)
  }
  signal.addEventListener('abort', retireOnAbort, { once: true })
  if (signal.aborted) retireOnAbort()

  let acquired = false
  try {
    while (!acquired) {
      throwIfAborted(signal)
      acquired = await tryAcquireFallback(owner)
      throwIfAborted(signal)
      if (!acquired) await waitForFallbackRetry(signal)
    }
  } catch (error) {
    signal.removeEventListener('abort', retireOnAbort)
    if (acquired) {
      // Abort can land after the final in-transaction marker check while the
      // put/commit is finishing. In that case the lease exists: delete it (or
      // keep the verified tombstone when IDB is transiently unavailable)
      // before this stale document exits.
      const released = await releaseFallbackReliably(owner)
      if (released) clearFallbackRetired(owner)
    } else {
      // Any queued acquisition has finished before its promise rejects or
      // returns. It observed the marker and could not write, so this marker is
      // no longer needed.
      clearFallbackRetired(owner)
    }
    throw error
  }
  traceFallback(owner, 'acquired')
  onAcquired()

  let lost = false
  let closing = false
  let renewal: Promise<void> | null = null
  const markLost = (): void => {
    if (lost) return
    lost = true
    requireInterruptedAuthTransitionReload(!signal.aborted)
    // Отменяем transition HTTP и не даём старой epoch продолжить после
    // freeze, если lease уже забрала другая вкладка.
    fenceAuthTransition()
  }
  const ensureOwned: AuthTransitionGuard = async () => {
    traceFallback(owner, 'ensure-start')
    if (signal.aborted || lost || !(await renewFallback(owner))) {
      markLost()
      traceFallback(owner, 'ensure-lost')
      throw signal.aborted ? abortError(signal) : new DOMException('auth transition lease lost', 'AbortError')
    }
    traceFallback(owner, 'ensure-complete')
  }
  const renew = setInterval(() => {
    if (renewal || lost || closing) return
    if (signal.aborted) {
      markLost()
      return
    }
    traceFallback(owner, 'renew-start')
    // Та же атомарная transaction либо продлевает нашу lease, либо видит,
    // что epoch уже потеряна. Отказ IDB не превращаем в unhandled rejection.
    const running: Promise<void> = renewFallback(owner)
      .then((owned) => {
        if (!owned) markLost()
      })
      .catch(markLost)
    renewal = running
    void running.finally(() => {
      traceFallback(owner, 'renew-complete')
      if (renewal === running) renewal = null
    })
  }, FALLBACK_RENEW_MS)
  let taskResult!: T
  let taskFailed = false
  let taskError: unknown
  try {
    taskResult = await task(ensureOwned)
  } catch (error) {
    taskFailed = true
    taskError = error
  }

  closing = true
  traceFallback(owner, 'closing')
  clearInterval(renew)
  // Safari can finish an IndexedDB renewal after a competing delete
  // transaction. Releasing first would let that late write resurrect a
  // 15-second lease after auth_transition_complete. Drain the only in-flight
  // renewal, forbid another one, and delete last.
  await (renewal as Promise<void> | null)?.catch(() => undefined)
  // `auth_transition_complete` is delivered as a queued storage event. Set a
  // synchronous handoff barrier before the first async release attempt, so a
  // reloading peer never waits behind a transient Safari IDB failure.
  retireFallback(owner)
  traceFallback(owner, 'release-start')
  let releaseError: unknown
  try {
    const released = await releaseFallbackReliably(owner)
    traceFallback(owner, released ? 'release-complete' : 'release-retired')
    if (released) clearFallbackRetired(owner)
  } catch (error) {
    releaseError = error
    traceFallback(owner, 'release-error', error instanceof Error ? error.name : 'unknown')
  }
  signal.removeEventListener('abort', retireOnAbort)

  // Preserve the original task failure. A successful task must still fail
  // closed when neither IDB release nor a verified tombstone was possible.
  if (taskFailed) throw taskError
  if (releaseError) throw releaseError
  return taskResult
}

function parseStoredAuthTransition(raw: string | null): StoredAuthTransition | null {
  if (!raw) return null
  try {
    const value = JSON.parse(raw) as Partial<StoredAuthTransition>
    const message = value.message
    if (
      (message?.type !== 'auth_transition_started' && message?.type !== 'auth_transition_complete') ||
      typeof message.transition_id !== 'string' ||
      typeof message.source_tab_id !== 'string' ||
      typeof value.nonce !== 'string' ||
      typeof value.sent_at !== 'number'
    ) {
      return null
    }
    return { message, nonce: value.nonce, sent_at: value.sent_at }
  } catch {
    return null
  }
}

function readStoredAuthTransition(): StoredAuthTransition | null {
  try {
    return parseStoredAuthTransition(localStorage.getItem(AUTH_TRANSITION_EVENT_KEY))
  } catch {
    return null
  }
}

// Снимок в момент загрузки — baseline этой вкладки. Более новый terminal,
// пропущенный во freeze/bfcache, будет прочитан на pageshow ровно один раз.
let observedStorageNonce = readStoredAuthTransition()?.nonce ?? null
let interruptedAuthTransitionNeedsReload = false
const interruptedAuthTransitionReloadListeners = new Set<() => void>()

function requireInterruptedAuthTransitionReload(immediate: boolean): void {
  interruptedAuthTransitionNeedsReload = true
  if (!immediate) return
  for (const listener of interruptedAuthTransitionReloadListeners) {
    try {
      listener()
    } catch {
      /* Reload listener не должен мешать fail-closed fence. */
    }
  }
}

/**
 * Source-вкладка синхронно fence'ится при pagehide/freeze. После
 * возврата из bfcache такой document нельзя оживлять вручную: bootstrap
 * должен заново сверить cookie и durable intents.
 */
export function consumeInterruptedAuthTransition(): boolean {
  const pending = interruptedAuthTransitionNeedsReload
  interruptedAuthTransitionNeedsReload = false
  return pending
}

/** Видимая вкладка перезагружается сразу после потери fallback lease. */
export function onInterruptedAuthTransitionReload(listener: () => void): () => void {
  interruptedAuthTransitionReloadListeners.add(listener)
  return () => interruptedAuthTransitionReloadListeners.delete(listener)
}

function publishAuthTransition(message: AuthTransitionMessage): boolean {
  const channelSent = postTabMessage(message)
  let storageSent = false
  try {
    const nonce = uuid()
    localStorage.setItem(AUTH_TRANSITION_EVENT_KEY, JSON.stringify({ message, nonce, sent_at: Date.now() }))
    observedStorageNonce = nonce
    storageSent = true
  } catch {
    // Если недоступны и BC, и storage, cookie mutation запрещаем ниже.
  }
  return channelSent || storageSent
}

export function onAuthTransitionStorageMessage(
  listener: (message: AuthTransitionMessage) => void,
): () => void {
  const handler = (event: StorageEvent): void => {
    if (event.key !== AUTH_TRANSITION_EVENT_KEY || !event.newValue) return
    const value = parseStoredAuthTransition(event.newValue)
    if (!value) return
    observedStorageNonce = value.nonce
    if (value.message.source_tab_id !== TAB_ID) listener(value.message)
  }
  globalThis.addEventListener('storage', handler)
  return () => globalThis.removeEventListener('storage', handler)
}

/** Terminal auth event, который вкладка пропустила во freeze/bfcache. */
export function consumeMissedAuthTransition(): AuthTransitionMessage | null {
  const value = readStoredAuthTransition()
  if (!value || value.nonce === observedStorageNonce) return null
  observedStorageNonce = value.nonce
  return value.message.source_tab_id === TAB_ID ? null : value.message
}

/** Web Locks сериализует переходы между вкладками; fallback — внутри одной. */
export async function withAuthTransition<T>(
  task: (ensureOwned: AuthTransitionGuard) => Promise<T>,
): Promise<T> {
  return withAuthTransitionOptions(task, { fenceOtherTabs: true, diagnosticName: 'session-mutation' })
}

export async function withAuthTransitionOptions<T>(
  task: (ensureOwned: AuthTransitionGuard) => Promise<T>,
  options: {
    fenceOtherTabs: boolean
    acquireTimeoutMs?: number
    /** Gated device-test trace label; never contains account or message data. */
    diagnosticName?: string
    /**
     * Выполняется под origin-wide mutex, но до закрытия текущей
     * user DB и session traffic. Отказ preflight оставляет активную
     * сессию нетронутой.
     */
    prepareBeforeFence?: (ensureOwned: AuthTransitionGuard) => Promise<void>
  },
): Promise<T> {
  const locks = navigator.locks as LockManager | undefined
  const run = async (leaseGuard: AuthTransitionGuard = async () => undefined): Promise<T> => {
    const transitionId = uuid()
    let interrupted = false
    const ensureOwned: AuthTransitionGuard = async () => {
      if (interrupted) throw new DOMException('auth transition interrupted', 'AbortError')
      await leaseGuard()
      if (interrupted) throw new DOMException('auth transition interrupted', 'AbortError')
    }

    const interrupt = (): void => {
      interrupted = true
      requireInterruptedAuthTransitionReload(false)
      // freeze/pagehide приходит до фактической приостановки документа.
      // Немедленно закрываем session traffic, user DB и лидерство: WebKit
      // способен оставить старый документ в bfcache, а открытая реплика тогда
      // блокирует delete/open нового bootstrap даже после hard navigation.
      fenceAuthTransition()
    }
    document.addEventListener('freeze', interrupt)
    globalThis.addEventListener('pagehide', interrupt)
    waiting.signal.addEventListener('abort', interrupt, { once: true })

    if (!options.fenceOtherTabs) {
      try {
        if (options.prepareBeforeFence) {
          await ensureOwned()
          await options.prepareBeforeFence(ensureOwned)
          await ensureOwned()
        }
        await ensureOwned()
        const result = await task(ensureOwned)
        await ensureOwned()
        return result
      } finally {
        document.removeEventListener('freeze', interrupt)
        globalThis.removeEventListener('pagehide', interrupt)
        waiting.signal.removeEventListener('abort', interrupt)
        if (interrupted) fenceAuthTransition()
      }
    }

    // Сначала доказываем, что remote-вкладки можно закрыть. Если ни
    // BroadcastChannel, ни storage недоступны, source resources ещё целы,
    // а durable prepare ещё не запускался.
    const announced = publishAuthTransition({
      type: 'auth_transition_started',
      transition_id: transitionId,
      source_tab_id: TAB_ID,
    })
    if (!announced) {
      document.removeEventListener('freeze', interrupt)
      globalThis.removeEventListener('pagehide', interrupt)
      waiting.signal.removeEventListener('abort', interrupt)
      throw new Error('cross-tab auth transition channel is unavailable')
    }

    let sourceFenced = false
    try {
      // Remote-вкладки уже fence'd, origin-wide mutex удерживается, но
      // source UI/DB остаются живы до успешного durable preflight.
      if (options.prepareBeforeFence) {
        await ensureOwned()
        await options.prepareBeforeFence(ensureOwned)
        await ensureOwned()
      }

      // Теперь и source, и remote блокируют обычный session traffic.
      // Только явно помеченные verify/logout запросы получают bypass.
      fenceAuthTransition()
      sourceFenced = true
      await ensureOwned()
      const result = await task(ensureOwned)
      await ensureOwned()
      return result
    } finally {
      document.removeEventListener('freeze', interrupt)
      globalThis.removeEventListener('pagehide', interrupt)
      waiting.signal.removeEventListener('abort', interrupt)
      // Terminal отправляется и при invalid OTP/network error: remote вкладка
      // не остаётся навсегда с закрытой БД и transport gate.
      publishAuthTransition({
        type: 'auth_transition_complete',
        transition_id: transitionId,
        source_tab_id: TAB_ID,
      })
      try {
        if (sourceFenced && !interrupted) {
          await ensureOwned()
          resumeApiSessionRequests()
        } else if (interrupted) {
          fenceAuthTransition()
        }
      } catch {
        // Потерявшая lease вкладка остаётся fail-closed до reload.
        fenceAuthTransition()
      }
    }
  }

  // Ожидание lock тоже является частью lifecycle: скрытая/замороженная вкладка
  // не должна позднее получить mutex и выполнить устаревшую cookie mutation.
  const waiting = new AbortController()
  let acquired = false
  let acquireTimer: ReturnType<typeof setTimeout> | undefined
  const markAcquired = (): void => {
    acquired = true
    clearTimeout(acquireTimer)
  }
  const abortWaiting = (): void => {
    // Вкладка может уйти в bfcache ещё до получения mutex. Её открытая
    // пользовательская БД всё равно не должна блокировать cleanup владельца.
    requireInterruptedAuthTransitionReload(false)
    fenceAuthTransition()
    if (!waiting.signal.aborted) {
      waiting.abort(new DOMException('auth transition interrupted while waiting', 'AbortError'))
    }
  }
  document.addEventListener('freeze', abortWaiting)
  globalThis.addEventListener('pagehide', abortWaiting)
  if (options.acquireTimeoutMs && options.acquireTimeoutMs > 0) {
    acquireTimer = setTimeout(() => {
      if (!acquired && !waiting.signal.aborted) {
        waiting.abort(new DOMException('auth transition lock acquisition timed out', 'TimeoutError'))
      }
    }, options.acquireTimeoutMs)
  }
  try {
    if (!locks?.request) {
      return await withFallbackMutex(run, waiting.signal, markAcquired, options.diagnosticName ?? 'unnamed')
    }
    return await locks.request(AUTH_TRANSITION_LOCK, { mode: 'exclusive', signal: waiting.signal }, () => {
      markAcquired()
      return run(async () => {
        throwIfAborted(waiting.signal)
      })
    })
  } finally {
    clearTimeout(acquireTimer)
    document.removeEventListener('freeze', abortWaiting)
    globalThis.removeEventListener('pagehide', abortWaiting)
  }
}

/** Синхронная граница для local и remote auth-transition_started. */
export function fenceAuthTransition(blockNewRequests = true): void {
  const safely = (action: () => void): void => {
    try {
      action()
    } catch {
      /* Lifecycle fence освобождает остальные ресурсы даже при локальном сбое. */
    }
  }
  safely(() => (blockNewRequests ? suspendApiSessionRequests() : invalidateApiSessionRequests()))
  safely(() => syncEngine()?.stop())
  safely(() => setSyncEngine(null))
  safely(() => setApiSessionOwner(null))
  safely(closeUserDb)
  safely(releaseLeadership)
}

export function finishRemoteAuthTransition(): void {
  resumeApiSessionRequests()
}

/** После authoritative `/me` source может запустить новую user epoch. */
export function resumeLocalAuthTransitionRequests(): void {
  resumeApiSessionRequests()
}
